import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  Pencil,
  Power,
  PowerOff,
  Code,
  Type,
  ArrowRight,
  Info,
} from 'lucide-react';

import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useMasterData } from '../contexts/MasterDataContext';
import { API } from '../utils/apiConfig';

const EMPTY_FORM = {
  alias_pattern: '',
  canonical_venue: '',
  is_regex: false,
  is_active: true,
  notes: '',
};

export default function VenueAliasesPage() {
  const { venues } = useMasterData();
  const [aliases, setAliases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchAliases = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/masters/venue-aliases`);
      setAliases(res.data || []);
    } catch (err) {
      console.error('Fetch aliases failed', err);
      toast.error('Failed to load venue aliases');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAliases();
  }, [fetchAliases]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (alias) => {
    setEditingId(alias.id);
    setForm({
      alias_pattern: alias.alias_pattern,
      canonical_venue: alias.canonical_venue,
      is_regex: alias.is_regex,
      is_active: alias.is_active,
      notes: alias.notes || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.alias_pattern.trim() || !form.canonical_venue) {
      toast.error('Alias pattern and canonical venue are required');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/masters/venue-aliases/${editingId}`, form);
        toast.success('Alias updated');
      } else {
        await axios.post(`${API}/masters/venue-aliases`, form);
        toast.success('Alias created');
      }
      setShowForm(false);
      setForm(EMPTY_FORM);
      setEditingId(null);
      fetchAliases();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save alias');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (alias) => {
    try {
      await axios.put(`${API}/masters/venue-aliases/${alias.id}`, {
        is_active: !alias.is_active,
      });
      toast.success(`Alias ${!alias.is_active ? 'enabled' : 'disabled'}`);
      fetchAliases();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to toggle');
    }
  };

  const handleDelete = async (alias) => {
    if (!window.confirm(`Delete alias "${alias.alias_pattern}" → "${alias.canonical_venue}"?`)) return;
    try {
      await axios.delete(`${API}/masters/venue-aliases/${alias.id}`);
      toast.success('Alias deleted');
      fetchAliases();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="venue-aliases-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-heading font-bold text-3xl text-slate-900 tracking-tight">
              Venue Aliases
            </h1>
            <p className="text-slate-600 mt-1 max-w-2xl text-sm">
              Map alternative venue names that appear in booking emails (e.g., District / Khelomore
              / Hudle invoice formats) to your canonical MAKS venues. The email parser applies
              these as a deterministic post-process safety net — overriding the LLM if it guesses
              the wrong venue.
            </p>
          </div>
          <Button
            onClick={openCreate}
            className="bg-primary hover:bg-primary-hover gap-2"
            data-testid="alias-add-btn"
          >
            <Plus className="w-4 h-4" />
            Add Alias
          </Button>
        </div>

        <Card className="p-3 bg-blue-50 border-blue-200 text-sm text-blue-900 flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            <strong>How matching works:</strong> if any alias pattern is found in the email body,
            the parsed venue is forced to the canonical name. Use <em>Substring</em> mode for
            simple text matches (case-insensitive); use <em>Regex</em> when you need flexible
            patterns. Order does not matter — first active match wins.
          </span>
        </Card>

        <Card className="overflow-hidden" data-testid="alias-table-card">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading…</div>
          ) : aliases.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              No aliases yet. Click <strong>Add Alias</strong> above to create your first one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Alias Pattern</th>
                    <th className="px-2 py-3 text-center font-semibold w-16">Mode</th>
                    <th className="px-2 py-3 text-center font-semibold w-8"></th>
                    <th className="px-4 py-3 text-left font-semibold">Maps To</th>
                    <th className="px-3 py-3 text-left font-semibold">Notes</th>
                    <th className="px-3 py-3 text-center font-semibold w-28">Active</th>
                    <th className="px-3 py-3 text-right font-semibold w-32">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {aliases.map((a) => (
                    <tr
                      key={a.id}
                      className={`border-t border-slate-100 ${a.is_active ? '' : 'opacity-60'}`}
                      data-testid={`alias-row-${a.id}`}
                    >
                      <td className="px-4 py-3 font-mono text-xs text-slate-800">
                        {a.alias_pattern}
                      </td>
                      <td className="px-2 py-3 text-center">
                        {a.is_regex ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700"
                            title="Regex match"
                          >
                            <Code className="w-3 h-3" /> RE
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600"
                            title="Substring match (case-insensitive)"
                          >
                            <Type className="w-3 h-3" /> Sub
                          </span>
                        )}
                      </td>
                      <td className="px-1 py-3 text-center text-slate-400">
                        <ArrowRight className="w-4 h-4 inline" />
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-emerald-700">
                        {a.canonical_venue}
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-500 max-w-xs truncate">
                        {a.notes || '—'}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <Switch
                          checked={a.is_active}
                          onCheckedChange={() => handleToggleActive(a)}
                          data-testid={`alias-toggle-${a.id}`}
                        />
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEdit(a)}
                            data-testid={`alias-edit-${a.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(a)}
                            className="text-red-600 hover:bg-red-50"
                            data-testid={`alias-delete-${a.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Stats */}
        {aliases.length > 0 && (
          <div className="text-xs text-slate-500" data-testid="alias-stats">
            {aliases.filter((a) => a.is_active).length} active /{' '}
            {aliases.length} total
          </div>
        )}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg" data-testid="alias-form-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingId ? 'Edit Alias' : 'New Venue Alias'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="alias_pattern">
                Alias pattern{' '}
                <span className="text-slate-400 font-normal">(text from email body)</span>
              </Label>
              <Input
                id="alias_pattern"
                value={form.alias_pattern}
                onChange={(e) => setForm({ ...form, alias_pattern: e.target.value })}
                placeholder="MAKS BADMINTON - MIRA ROAD"
                className="font-mono text-sm"
                data-testid="alias-pattern-input"
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50">
              <div className="text-sm">
                <p className="font-medium text-slate-800">Use regex</p>
                <p className="text-xs text-slate-500">
                  {form.is_regex
                    ? 'Pattern is a regular expression (case-insensitive).'
                    : 'Pattern is matched as a case-insensitive substring.'}
                </p>
              </div>
              <Switch
                checked={form.is_regex}
                onCheckedChange={(v) => setForm({ ...form, is_regex: v })}
                data-testid="alias-is-regex-switch"
              />
            </div>

            <div>
              <Label htmlFor="canonical_venue">Maps to canonical venue</Label>
              <Select
                value={form.canonical_venue}
                onValueChange={(v) => setForm({ ...form, canonical_venue: v })}
              >
                <SelectTrigger
                  id="canonical_venue"
                  data-testid="alias-canonical-venue-select"
                >
                  <SelectValue placeholder="Select MAKS venue" />
                </SelectTrigger>
                <SelectContent>
                  {venues.filter((v) => v.is_active).map((v) => (
                    <SelectItem key={v.id} value={v.name}>
                      {v.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="notes">Notes (optional)</Label>
              <Input
                id="notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="District badminton format from May 2026"
                data-testid="alias-notes-input"
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
              <div className="text-sm flex items-center gap-2">
                {form.is_active ? <Power className="w-4 h-4 text-emerald-600" /> : <PowerOff className="w-4 h-4 text-slate-400" />}
                <span className="font-medium text-slate-800">
                  {form.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                data-testid="alias-is-active-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="alias-save-btn">
              {saving ? 'Saving…' : editingId ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
